import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ENVIRONMENT_TOKEN,
  REPORT_ERROR_TOKEN,
} from '@merchant-portal/app/core/services/global-token';
import { Meta } from '@merchant-portal/app/models/setting/team-member.interface';
import { UtilService } from '@merchant-portal/app/services/util.service';

export interface IXBTransactionData {
  uuid: string;
  sourceCurrency: string;
  destinationCurrency: string;
  destinationAmount: string;
  sourceAmount: string;
  fee: string;
  totalAmount: string;
  createdAt: string;
  status: string;
  beneficiaryAccountName: string;
}

interface ApiResponse<T> {
  code: string;
  message: string;
  data: T;
  meta: Meta;
}
export interface IXBTransactionHistoryFilter {
  uuid: string;
  startDate: string;
  endDate: string;
  status: string;
  sort: string;
  sortBy: string;
  page: number;
  perPage: number;
}

export interface ICreateXbTransactionData {
  senderData: SenderData;
  beneficiaryData: BeneficiaryData;
  sourceCurrency: string;
  destinationCurrency: string;
  destinationAmount: string;
  purposeCode: string;
  remark: string;
  routingValue: string;
  cnaps?: string;
}

export interface SenderData {
  name: string;
  countryCode: string;
  state: string;
  city: string;
  address: string;
  postcode: string;
  accountType: string;
  identificationType: string;
  identificationNumber: string;
  bankAccountNumber?: string;
  dob?: string;
  contactCountryCode: string;
  contactNumber: string;
  sourceOfIncome: string;
}

export interface BeneficiaryData {
  name: string;
  countryCode: string;
  state: string;
  city: string;
  address: string;
  postcode: string;
  accountType: string;
  identificationType: string;
  identificationNumber: string;
  accountNumber: string;
  bankName: string;
  bankCode: string;
  contactCountryCode?: string;
  contactNumber?: string;
  email?: string;
  transferMethod?: string;
  payoutMethod?: string;
}

interface IXBFxRateParams {
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface IXBFxRateResponse {
  fxRate: string;
  destinationFxRate: string;
  expiryAt: string;
}

export interface IXBConfig {
  uuid: string;
  merchantId: string;
  paymentMethod: string | null;
  amount: number;
  amountType: string;
  percentage: number;
  reference: string;
  referenceType: string;
  deductionType: string;
  taxType: string;
  taxPercentage: number;
  settlementModel: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IXBConfigResponse {
  local: IXBConfig;
  swift: IXBConfig;
}

export interface XBTransactionDetail {
  uuid: string;
  merchantId: string;
  referenceId: string;
  sourceCurrency: string;
  destinationCurrency: string;
  destinationAmount: string;
  fxRate: string;
  destinationFxRate: string;
  sourceAmount: string;
  fee: string;
  totalAmount: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  statusDescription: string;
  senderData: SenderDataDetail;
  beneficiaryId: string;
  beneficiaryData: BeneficiaryDataDetail;
  purposeCode: string;
  remark: string;
  expiredAt: string;
  routingCode?: string;
  routingValue?: string;
  statusHistory?: StatusHistoryItem[];
}

export interface SenderDataDetail {
  name: string;
  country: string;
  state: string;
  city: string;
  address: string;
  postcode: string;
  accountType: string;
  identificationType: string;
  identificationNumber: string;
  contactNumber?: string;
  contactCountryCode?: string;
  countryName?: string;
  sourceOfIncome: string;
  dob?: string;
  bankAccountNumber?: string;
}

export interface BeneficiaryDataDetail {
  name: string;
  address: string;
  city: string;
  postcode: string;
  state: string;
  country: string;
  accountNumber: string;
  bankName: string;
  bankCode: string;
  transferMethod: string;
  transferMethodCode: string;
  accountType: string;
  contactNumber?: string;
  contactCountryCode?: string;
  countryName?: string;
  email?: string;
}

export interface StatusHistoryItem {
  status: string;
  label: string;
  description?: string;
  recommendation?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class XbApiService {
  private http = inject(HttpClient);
  private url = inject(ENVIRONMENT_TOKEN).backend_portal_url;
  private util = inject(UtilService);

  getTransactionHistoryList(filter: Partial<IXBTransactionHistoryFilter>) {
    return this.http.get<ApiResponse<IXBTransactionData[]>>(this.url + 'api/v1/xb/payout', {
      params: this.util.deleteUndefinedKey(filter),
    });
  }

  createTransaction(data: ICreateXbTransactionData) {
    const context = new HttpContext();
    context.set(REPORT_ERROR_TOKEN, 'Create Xb Payout');
    return this.http.post<ApiResponse<IXBTransactionData>>(this.url + 'api/v1/xb/payout', data, {
      context,
    });
  }

  requestFxRate(props: IXBFxRateParams) {
    return this.http.get<ApiResponse<IXBFxRateResponse>>(this.url + 'api/v1/xb/fx-rate', {
      params: props as any,
    });
  }

  getXbConfig() {
    return this.http.get<ApiResponse<IXBConfigResponse>>(this.url + 'api/v1/xb/configs');
  }

  requestTransactionDetail(uuid: string) {
    return this.http.get<ApiResponse<XBTransactionDetail>>(this.url + 'api/v1/xb/payout/' + uuid);
  }

  confirmTransaction(uuid: string) {
    return this.http.post<ApiResponse<XBTransactionDetail>>(
      this.url + 'api/v1/xb/payout/' + uuid + '/confirm',
      {}
    );
  }

  uploadDocument(payoutUuid: string, document: File) {
    const context = new HttpContext();
    context.set(REPORT_ERROR_TOKEN, 'Upload XB Document');

    const formData = new FormData();
    formData.append('document', document);

    return this.http.post<ApiResponse<any>>(
      this.url + 'api/v1/xb/payout/' + payoutUuid + '/upload',
      formData,
      {
        context,
      }
    );
  }

  exportPayoutHistory(params: { startAt: string; endAt: string; status?: string }) {
    return this.http.post<ApiResponse<{ url: string }>>(
      this.url + 'api/v1/xb/payout/export',
      params
    );
  }

  getDocumentUploadConfig() {
    return this.http.get<ApiResponse<{ maxFileSize: number }>>(
      this.url + '/api/v1/xb/payout/upload/limit'
    );
  }
}
